import { consts } from '@allesauto/utils/consts';
import React, { FC, useMemo } from 'react';

import { ResponsiveImage } from '../responsiveImage';
import { getResizedImageUrl, Transformation } from '../responsiveImage';

export const supportedAspectRatios = {
  '16_BY_10': 16 / 10,
  '1_BY_1': 1,
  '4_BY_3': 4 / 3,
} as const;

export type SupportedAspectRatio = keyof typeof supportedAspectRatios;

export type VehicleImageProps = {
  alt?: string;
  aspectRatio?: SupportedAspectRatio;
  backgroundColor?: string;
  className?: string;
  filename?: string | null;
  transformation?: Transformation;
  fallbackImage?: 'pcp_pdp' | 'garage' | 'trade_in_widget';
  isWhiteLabel?: boolean;
  isLazyLoaded?: boolean;
  pdPCarsImageAfterConfiguration?: boolean;
};

const VEHICLE_IMAGE_WIDTHS = [32, 64, 128, 256, 512, 1024, 2048];

/**
 * Generic implementation of a responsive Vehicle image.
 *
 * `filename` should be a path relative to the S3 bucket of the AA image resize service,
 * e.g. `EAS_164/EAS_164_W0V0XEP68K4353508_1_2073171837_cut.jpeg`.
 *
 * If the `filename` is omitted, the component renders the globally configured fallback
 * image instead. Beware that the fallback image is static and will not be transformed
 * or resized when rendered.
 *
 * The component relies on the `ResponsiveImage` for rendering. The `alt`, `aspectRatio`,
 * `backgroundColor` and `transformation` properties are simply passed through to the
 * `ResponsiveImage`, but have been configured with generic defaults here, so that
 * they can be omitted most of the time.
 */
const VehicleImage: FC<VehicleImageProps> = ({
  alt = '',
  aspectRatio = '16_BY_10',
  backgroundColor,
  className,
  filename,
  transformation = 'contain',
  fallbackImage = 'garage',
  isWhiteLabel = false,
  isLazyLoaded,
  pdPCarsImageAfterConfiguration = false,
}) => {
  const fallbackImgBaseURL =
    process.env.CONTENT_IMAGE_BASE_URL ||
    process.env.NEXT_PUBLIC_CONTENT_IMAGE_BASE_URL ||
    '';

  let fallbackImgFilename = consts.FALLBACK_IMAGES.GARAGE_VEHICLE;

  if (!isWhiteLabel) {
    switch (fallbackImage) {
      case 'pcp_pdp':
        fallbackImgFilename = consts.FALLBACK_IMAGES.PCP_PDP;
        break;
      case 'trade_in_widget':
        fallbackImgFilename = consts.FALLBACK_IMAGES.TRADE_IN_WIDGET;
        break;
      default:
        fallbackImgFilename = consts.FALLBACK_IMAGES.GARAGE_VEHICLE;
        break;
    }
  }

  const fallbackImgPath = fallbackImgBaseURL + '/' + fallbackImgFilename;

  const cdSystemeImage = filename?.startsWith('https://api.cd-systeme.com/');

  const imageSrc = useMemo(() => {
    if (!filename || filename === '/') {
      return fallbackImgPath;
    }

    // return the original path as it comes for absolute paths
    if (filename.startsWith('http')) {
      return filename;
    }

    return VEHICLE_IMAGE_WIDTHS.map((width) => ({
      width,
      src: getResizedImageUrl(
        filename,
        width,
        width / supportedAspectRatios[aspectRatio],
        transformation,
        backgroundColor
      ),
    }));
  }, [aspectRatio, backgroundColor, fallbackImgPath, filename, transformation]);

  return (
    <ResponsiveImage
      alt={alt}
      aspectRatio={supportedAspectRatios[aspectRatio]}
      className={className}
      src={imageSrc}
      // if imageSrc is a string then it was not resized and we need to fit it into the defined aspect ratio
      style={{
        ...(typeof imageSrc === 'string' ? { objectFit: 'contain' } : {}),
        ...(cdSystemeImage && pdPCarsImageAfterConfiguration
          ? {
              objectFit: 'contain',
              transform: 'scale(1.5)',
              background: 'none',
            }
          : {}),
      }}
      loading={isLazyLoaded ? 'lazy' : 'eager'}
    />
  );
};

export default VehicleImage;
